import React, { useMemo, useState, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { CtyButton, CtySpinner, useHasApplicationResource } from '@circuitry-ai/doc-ui';
import { Select, SelectItem } from '@nextui-org/react';
import {
  useGetFgaAllResourcesQuery,
  useGetFgaGroupsQuery
} from "../../../../shared/data/src/features/settings/settingsApiSlice";
import { AgGridWrapper } from '../components/CellRenderers';
import EditResourceAccessModal from '../components/EditResourceAccessModal';
import { buildAllResourcesWithData, type ResourceType } from '../fga-data-utils';

// Cell renderers
const ResourceNameCellRenderer = (params: any) => <div className="font-medium text-foreground">{params.value}</div>;

const AssignedGroupsCellRenderer = (params: any) => {
  const groups = params.data?.assignedGroups || [];
  if (groups.length === 0) return <span className="text-default-400 text-sm">No groups assigned</span>;

  return (
    <div className="flex flex-wrap gap-1 items-center h-full">
      {groups.map((group: string) => (
        <span key={group} className="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-default-100 text-default-700">
          {group}
        </span>
      ))}
    </div>
  );
};

const ResourceTypeCellRenderer = (params: any) => {
  const resourceType = params.value as ResourceType;
  const typeLabels: Record<ResourceType, string> = {
    'application_resource': 'Application Resource',
    'entity_type': 'Entity Type',
    'entity_resource': 'Entity Resource'
  };
  return <div className="font-medium text-foreground">{typeLabels[resourceType] || resourceType}</div>;
};

const ActionCellRenderer = ({ params, onAction, isDisabled }: {
  params: any;
  onAction: (data: any) => void;
  isDisabled?: boolean;
}) => {
  const hasGroups = (params.data.assignedGroups?.length || 0) > 0;
  return (
    <CtyButton
      size="sm"
      variant="bordered"
      onPress={() => onAction(params.data)}
      startContent={<Icon icon={hasGroups ? "mdi:pencil" : "mdi:plus"} className="w-4 h-4" />}
      isDisabled={isDisabled}
    >
      {hasGroups ? 'Edit Access' : 'Assign Groups'}
    </CtyButton>
  );
};

const createActionCellRenderer = (onAction: (data: any) => void, isDisabled?: boolean) =>
  (params: any) => <ActionCellRenderer params={params} onAction={onAction} isDisabled={isDisabled} />;

type FilterType = 'all' | 'unassigned';
type ResourceTypeFilter = 'all' | 'application_resource' | 'entity_type' | 'entity_resource';

export const ResourcesTab: React.FC = () => {
  const hasAccessManagement = useHasApplicationResource('access_management');
  const [filter, setFilter] = useState<FilterType>('all');
  const [resourceTypeFilter, setResourceTypeFilter] = useState<ResourceTypeFilter>('all');
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>('all');
  const [selectedResource, setSelectedResource] = useState<{
    resource: string;
    resourceId: string;
    resourceType: ResourceType;
  } | null>(null);

  // API hooks
  const { data: allResourcesData, isLoading: resourcesLoading, refetch: refetchAllResources } = useGetFgaAllResourcesQuery(undefined, { refetchOnMountOrArgChange: true });
  const { data: groupsData, refetch: refetchGroups } = useGetFgaGroupsQuery(undefined, { refetchOnMountOrArgChange: true });
  const allGroups = groupsData?.data?.groups || [];

  const resourcesWithData = useMemo(
    () => buildAllResourcesWithData(allResourcesData?.data?.resources, allGroups),
    [allResourcesData, allGroups]
  );

  // Get unique entity types from entity resources
  const entityTypes = useMemo(() => {
    const types = new Set<string>();
    resourcesWithData.forEach(resource => {
      if (resource.resourceType === 'entity_resource' && resource.entityType) {
        types.add(resource.entityType);
      }
    });
    return Array.from(types).sort((a, b) => a.localeCompare(b));
  }, [resourcesWithData]);

  // Filter resources
  const filteredResources = useMemo(() => {
    let filtered = resourcesWithData;
    // Filter by resource type
    if (resourceTypeFilter !== 'all') {
      filtered = filtered.filter(resource => resource.resourceType === resourceTypeFilter);
    }
    // Filter by entity type (only for entity resources)
    if (entityTypeFilter !== 'all' && resourceTypeFilter === 'entity_resource') {
      filtered = filtered.filter(resource => 
        resource.resourceType !== 'entity_resource' || resource.entityType === entityTypeFilter
      );
    }
    // Filter by assigned/unassigned
    if (filter === 'unassigned') {
      filtered = filtered.filter(resource => resource.assignedGroups.length === 0);
    }
    return filtered;
  }, [resourcesWithData, filter, resourceTypeFilter, entityTypeFilter]);

  // Handlers
  const handleOpenModal = useCallback((data: any) => {
    setSelectedResource({
      resource: data.resource,
      resourceId: data.resourceId,
      resourceType: data.resourceType,
    });
  }, []);

  const handleModalSuccess = useCallback(() => {
    refetchGroups();
    refetchAllResources();
    setSelectedResource(null);
  }, [refetchGroups, refetchAllResources]);

  const handleModalClose = useCallback(() => setSelectedResource(null), []);

  if (resourcesLoading) {
    return <div className="flex items-center justify-center py-8"><CtySpinner size="md" /></div>;
  }

  const columnDefs = [
    {
      headerName: 'Resource Name',
      field: 'resource',
      sortable: true,
      flex: 2,
      minWidth: 200,
      cellRenderer: ResourceNameCellRenderer
    },
    {
      headerName: 'Assigned Roles',
      field: 'assignedGroups',
      sortable: false,
      flex: 2,
      minWidth: 300,
      cellRenderer: AssignedGroupsCellRenderer
    },
    {
      headerName: 'Resource Type',
      field: 'resourceType',
      sortable: true,
      flex: 1,
      minWidth: 150,
      cellRenderer: ResourceTypeCellRenderer
    },
    {
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      minWidth: 180,
      maxWidth: 180,
      pinned: 'right' as const,
      lockPinned: true,
      lockPosition: true,
      resizable: false,
      cellRenderer: createActionCellRenderer(handleOpenModal, !hasAccessManagement)
    }
  ];

  const totalResources = resourcesWithData.length;
  const assignedResources = resourcesWithData.filter(r => r.assignedGroups.length > 0).length;
  const unassignedResources = totalResources - assignedResources;

  return (
    <>
      <AgGridWrapper rowData={filteredResources} columnDefs={columnDefs}>
        <div className="mb-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex flex-col gap-1">
              <h2 className="text-xl font-semibold">Resource Access Overview</h2>
              <p className="text-default-500">Click on assigned resources to edit permissions</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm text-default-500">
                <span className="font-medium">Total Resources: {totalResources}</span>
                <span className="ml-4 text-success font-medium">Assigned: {assignedResources}</span>
                <span className="ml-4 text-warning font-medium">Unassigned: {unassignedResources}</span>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="mb-4">
            <div className="flex items-center gap-2">
              <CtyButton
                size="sm"
                variant={filter === 'all' ? 'solid' : 'bordered'}
                color={filter === 'all' ? 'primary' : 'default'}
                onPress={() => setFilter('all')}
                startContent={<Icon icon="mdi:filter" className="w-4 h-4" />}
              >
                Show All
              </CtyButton>
              <CtyButton
                size="sm"
                variant={filter === 'unassigned' ? 'solid' : 'bordered'}
                color={filter === 'unassigned' ? 'primary' : 'default'}
                onPress={() => setFilter('unassigned')}
                startContent={<Icon icon="mdi:filter" className="w-4 h-4" />}
              >
                Show Unassigned
              </CtyButton>
              {filter === 'unassigned' && (
                <span className="text-sm text-default-500">
                  Showing {unassignedResources} unassigned resources
                </span>
              )}
              <Select
                size="sm"
                selectedKeys={new Set([resourceTypeFilter])}
                onSelectionChange={(keys) => {
                  const selected = Array.from(keys)[0] as ResourceTypeFilter;
                  setResourceTypeFilter(selected || 'all');
                  if (selected !== 'all' && selected !== 'entity_resource') {
                    setEntityTypeFilter('all');
                  }
                }}
                className="w-1/6 ml-auto"
                disallowEmptySelection
              >
                <SelectItem key="all" value="all">All Resources</SelectItem>
                <SelectItem key="application_resource" value="application_resource">Application Resources</SelectItem>
                <SelectItem key="entity_type" value="entity_type">Entity Types</SelectItem>
                <SelectItem key="entity_resource" value="entity_resource">Entity Resources</SelectItem>
              </Select>
              {resourceTypeFilter === 'entity_resource' && entityTypes.length > 0 && (
                <Select
                  size="sm"
                  selectedKeys={new Set([entityTypeFilter])}
                  onSelectionChange={(keys) => {
                    const selected = Array.from(keys)[0] as string;
                    setEntityTypeFilter(selected || 'all');
                  }}
                  className="w-1/6"
                  disallowEmptySelection
                >
                  <SelectItem key="all" value="all">All Entity Types</SelectItem>
                  <>
                    {entityTypes.map((entityType) => (
                      <SelectItem key={entityType} value={entityType} textValue={entityType}>
                       {entityType}
                      </SelectItem>
                    ))}
                  </>
                </Select>
              )}
            </div>
          </div>
        </div>
      </AgGridWrapper>

      {/* Edit Resource Access Modal */}
      {selectedResource && (
        <EditResourceAccessModal
          isOpen={!!selectedResource}
          onOpenChange={handleModalClose}
          resourceName={selectedResource.resource}
          resourceId={selectedResource.resourceId}
          resourceType={selectedResource.resourceType}
          onSuccess={handleModalSuccess}
        />
      )}

    </>
  );
};
